import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  LinearProgress,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  role_display: string;
  is_active: boolean;
  date_joined: string;
}

interface Company {
  id: number;
  name: string;
  cnpj: string | null;
  max_users: number;
  current_users_count: number;
  available_slots: number;
  subscription_status: string;
  subscription_status_display: string;
  subscription_expires_at: string | null;
}

const CompanyUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    password_confirm: '',
    role: 'sales_rep',
    is_active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load users and company info in parallel
      const [usersResponse, quotaResponse] = await Promise.all([
        api.get('/auth/users/'),
        api.get('/auth/users/quota_info/')
      ]);

      const usersData = usersResponse.data.results || usersResponse.data;
      setUsers(usersData);
      setCompany(quotaResponse.data);
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.detail || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        password: '',
        password_confirm: '',
        role: user.role,
        is_active: user.is_active
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        password_confirm: '',
        role: 'agent',
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      password_confirm: '',
        role: 'agent',
      is_active: true
    });
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingUser) {
        // Update existing user
        const dataToSend = {
          username: formData.username,
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          role: formData.role,
          is_active: formData.is_active
        };

        await api.patch(`/auth/users/${editingUser.id}/`, dataToSend);
        setSuccess('Usuário atualizado com sucesso!');
      } else {
        // Create new user
        if (formData.password !== formData.password_confirm) {
          setError('As senhas não coincidem');
          setSaving(false);
          return;
        }

        if (formData.password.length < 8) {
          setError('A senha deve ter pelo menos 8 caracteres');
          setSaving(false);
          return;
        }

        const dataToSend = {
          username: formData.username,
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          password: formData.password,
          password_confirm: formData.password_confirm,
          role: formData.role
        };

        await api.post('/auth/users/', dataToSend);
        setSuccess('Usuário criado com sucesso!');
      }

      handleCloseDialog();
      await loadData();
    } catch (err: any) {
      console.error('Error saving user:', err);
      console.error('Response data:', JSON.stringify(err.response?.data, null, 2));
      const responseData = err.response?.data;

      // Handle different error formats from Django REST Framework
      let errorMsg = 'Erro ao salvar usuário';

      if (responseData) {
        if (responseData.detail) {
          errorMsg = responseData.detail;
        } else if (responseData.error) {
          errorMsg = responseData.error;
        } else if (typeof responseData === 'object') {
          // Field-specific errors from serializer
          const errors: string[] = [];
          for (const [field, messages] of Object.entries(responseData)) {
            const fieldMessages = Array.isArray(messages) ? messages : [messages];
            const fieldName = field === 'password' ? 'Senha' :
                             field === 'password_confirm' ? 'Confirmação de senha' :
                             field === 'email' ? 'Email' :
                             field === 'username' ? 'Nome de usuário' :
                             field === 'first_name' ? 'Nome' :
                             field === 'last_name' ? 'Sobrenome' :
                             field === 'non_field_errors' ? '' : field;
            errors.push(`${fieldName}: ${fieldMessages.join(', ')}`);
          }
          errorMsg = errors.join(' | ');
        }
      }

      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Tem certeza que deseja desativar este usuário?')) return;

    try {
      await api.delete(`/auth/users/${userId}/`);
      setSuccess('Usuário desativado com sucesso!');
      await loadData();
    } catch (err: any) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.detail || 'Erro ao desativar usuário');
    }
  };

  const handleActivateUser = async (userId: number) => {
    try {
      await api.post(`/auth/users/${userId}/activate/`);
      setSuccess('Usuário reativado com sucesso!');
      await loadData();
    } catch (err: any) {
      console.error('Error activating user:', err);
      setError(err.response?.data?.detail || 'Erro ao reativar usuário');
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return '#ef4444';
      case 'manager': return '#f59e0b';
      case 'agent': return '#3b82f6';
      case 'viewer': return '#9ca3af';
      default: return '#9ca3af';
    }
  };

  const getRoleDescription = (role: string) => {
    switch (role) {
      case 'admin': return 'Acesso total ao sistema';
      case 'manager': return 'CRM, Comunicação, Automação e Relatórios da equipe';
      case 'agent': return 'CRM e Comunicação (apenas seus dados)';
      case 'viewer': return 'Apenas visualização de dados';
      default: return '';
    }
  };

  const getSubscriptionStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#22c55e';
      case 'trial': return '#3b82f6';
      case 'canceled': return '#ef4444';
      case 'expired': return '#9ca3af';
      default: return '#9ca3af';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Back to Settings */}
      <button
        onClick={() => navigate('/settings')}
        style={{
          display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-secondary)', fontSize: 'var(--text-sm)',
          padding: 'var(--space-1) 0', marginBottom: 'var(--space-4)',
          transition: 'color 0.2s'
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--primary-600)')}
        onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-secondary)')}
      >
        <ArrowLeft size={16} />
        Voltar para Configurações
      </button>

      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'var(--text-primary)' }}>
            Gestão de Usuários
          </Typography>
          <Typography variant="body1" sx={{ color: 'var(--text-secondary)' }}>
            Gerencie os usuários da sua empresa
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={!company || company.available_slots === 0}
          sx={{
            background: 'linear-gradient(135deg, var(--color-primary-600), var(--color-primary-700))',
            boxShadow: 'var(--shadow-lg)',
            '&:hover': { background: 'linear-gradient(135deg, var(--color-primary-700), var(--color-primary-800))' }
          }}
        >
          Novo Usuário
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Company Info Card */}
      {company && (
        <Card sx={{
          mb: 3,
          background: 'var(--glass-bg-strong)',
          backdropFilter: 'var(--glass-blur)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-2xl)',
          boxShadow: 'var(--shadow-glass)'
        }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <BusinessIcon sx={{ fontSize: 40, color: '#6366f1' }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {company.name}
                    </Typography>
                    {company.cnpj && (
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        CNPJ: {company.cnpj}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip
                    icon={<PersonIcon />}
                    label={`${company.current_users_count} usuários`}
                    size="small"
                    sx={{ backgroundColor: 'rgba(99,102,241,0.1)', color: '#6366f1' }}
                  />
                  <Chip
                    label={`Status: ${company.subscription_status_display}`}
                    size="small"
                    sx={{
                      backgroundColor: `${getSubscriptionStatusColor(company.subscription_status)}20`,
                      color: getSubscriptionStatusColor(company.subscription_status)
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ color: '#6b7280', mb: 1 }}>
                  Limite de Usuários
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {company.current_users_count} de {company.max_users}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      {company.available_slots} vagas
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(company.current_users_count / company.max_users) * 100}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: 'rgba(0,0,0,0.05)',
                      '& .MuiLinearProgress-bar': {
                        background: company.available_slots === 0
                          ? 'linear-gradient(90deg, #ef4444, #dc2626)'
                          : 'linear-gradient(90deg, #6366f1, #8b5cf6)'
                      }
                    }}
                  />
                </Box>
                {company.subscription_expires_at && (
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>
                    Expira em: {new Date(company.subscription_expires_at).toLocaleDateString('pt-BR')}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Users Table */}
      <Card sx={{
        background: 'var(--glass-bg-strong)',
        backdropFilter: 'var(--glass-blur)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-2xl)',
        boxShadow: 'var(--shadow-glass)'
      }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Usuários ({users.length})
          </Typography>

          {users.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PersonIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
              <Typography variant="body1" sx={{ color: '#6b7280' }}>
                Nenhum usuário cadastrado
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Função</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
                            {user.first_name.charAt(0) || user.username.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {user.first_name} {user.last_name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6b7280' }}>
                              @{user.username}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EmailIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                          <Typography variant="body2">{user.email}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.role_display}
                          size="small"
                          sx={{
                            backgroundColor: `${getRoleColor(user.role)}20`,
                            color: getRoleColor(user.role),
                            fontWeight: 600
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.is_active ? 'Ativo' : 'Inativo'}
                          size="small"
                          sx={{
                            backgroundColor: user.is_active ? 'rgba(34,197,94,0.1)' : 'rgba(156,163,175,0.1)',
                            color: user.is_active ? '#22c55e' : '#9ca3af'
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(user)}
                          sx={{ color: '#6366f1' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        {user.is_active ? (
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteUser(user.id)}
                            sx={{ color: '#ef4444' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleActivateUser(user.id)}
                            sx={{ fontSize: 11 }}
                          >
                            Reativar
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit User Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Nome de Usuário"
              fullWidth
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
              disabled={!!editingUser}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Nome"
                fullWidth
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              />
              <TextField
                label="Sobrenome"
                fullWidth
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              />
            </Box>
            {!editingUser && (
              <>
                <TextField
                  label="Senha"
                  type="password"
                  fullWidth
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  helperText="Mínimo 6 caracteres"
                />
                <TextField
                  label="Confirmar Senha"
                  type="password"
                  fullWidth
                  value={formData.password_confirm}
                  onChange={(e) => setFormData({ ...formData, password_confirm: e.target.value })}
                  required
                />
              </>
            )}
            <TextField
              label="Função"
              select
              fullWidth
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              required
              helperText={getRoleDescription(formData.role)}
            >
              <MenuItem value="agent">
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Agent</Typography>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>CRM e Comunicação</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="manager">
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Manager</Typography>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>CRM, Comunicação, Automação e Relatórios da equipe</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="admin">
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Admin</Typography>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>Acesso total ao sistema</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="viewer">
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Viewer</Typography>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>Apenas visualização</Typography>
                </Box>
              </MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {editingUser ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyUsers;